import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import moment from "moment";
import { getKpiTypeColor } from "../../../utils/kpiColors";
import { formatStartDateIso, formatEndDateIso } from "../../../utils/dateUtils";

const formatKpiPointsNotice = (kpiObj, kpiTypeObj, fallbackAssignedPoints = 0, isAssignee = false) => {
  if (!isAssignee) {
    return {
      type: 'success',
      message: '✓ Work Completed'
    };
  }

  if (!kpiObj || Object.keys(kpiObj).length === 0) return null;

  const assigned = Number(kpiObj.assigned_points ?? fallbackAssignedPoints ?? 0);
  const earned = Number(kpiObj.earned_points ?? assigned ?? 0);
  const isLate = Boolean(kpiObj.is_late_submission || kpiObj.transaction_type === 'DEDUCTION');

  if (isLate) {
    const delayScore = Number(kpiTypeObj?.task_delay_score ?? (assigned > earned ? assigned - earned : 25));
    return {
      type: 'warning',
      message: `⚠ Work submitted late. You earned ${earned} KPI points after the ${delayScore} point late submission penalty.`
    };
  } else {
    return {
      type: 'success',
      message: `✓ Work Completed! You earned +${earned} KPI points.`
    };
  }
};

const KPIWorkDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { workDetail, taskTypes, eligibleUsers, taskComments, taskHistory, loading } = useSelector((state) => state.kpi);
  const userInfoData = useSelector((state) => state.auth?.userInfoData || state.auth?.userInfo);
  const [statusList, setStatusList] = useState([]);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingWork, setDeletingWork] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [selectedStatusOverride, setSelectedStatusOverride] = useState(null);
  const [showCompleteConfirmModal, setShowCompleteConfirmModal] = useState(false);
  const [pendingStatusOption, setPendingStatusOption] = useState(null);
  const [scoringNotice, setScoringNotice] = useState(null);
  const [noticeDismissed, setNoticeDismissed] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);
  const [replyingCommentId, setReplyingCommentId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [submittingReply, setSubmittingReply] = useState(false);
  const [visibleCommentsCount, setVisibleCommentsCount] = useState(5);

  const [editForm, setEditForm] = useState({
    task_name: "",
    task_description: "",
    task_priority: "Medium",
    task_due_date: "",
    assigned_kpi_points: "",
    task_kpi_type_id: "",
    task_status: "",
    assigned_to: []
  });

  useEffect(() => {
    if (id) {
      setScoringNotice(null);
      setNoticeDismissed(false);
      setSelectedStatusOverride(null);
      setFetchError(null);
      dispatch.kpi.getMyWorkDetail(id).catch((err) => {
        const status = err?.response?.status || err?.status || 403;
        setFetchError({
          status,
          message: err?.response?.data?.message || err?.message || (status === 403 ? "Forbidden: Access Denied" : "Page Not Found")
        });
      });
      dispatch.kpi.getTaskHistory(id);
      dispatch.kpi.getTaskComments({ task_id: id });
    }
    dispatch.kpi.getTaskTypes();
    dispatch.trashandStatus.getStatusData().then(res => {
      if (res && res.data) {
        setStatusList(res.data);
      }
    });
  }, [dispatch, id]);

  const executeStatusChange = async (selectedOption) => {
    if (!selectedOption || statusUpdating) return;
    const selectedStatusId = selectedOption.value;
    const selectedStatusLabel = selectedOption.label;

    setSelectedStatusOverride(selectedOption);
    setStatusUpdating(true);
    setScoringNotice(null);
    try {
      await dispatch.kpi.updateWorkStatus({
        task_id: work._id || id,
        task_status: selectedStatusId,
        hideLoading: true
      });

      const updatedDetailRes = await dispatch.kpi.getMyWorkDetail({ id, hideLoading: true });
      dispatch.kpi.getTaskHistory(id);

      const updatedKpi = updatedDetailRes?.data?.kpi || updatedDetailRes?.kpi || workDetail?.kpi || {};
      const updatedKpiType = updatedDetailRes?.data?.kpi_type || updatedDetailRes?.kpi_type || work?.kpi_type || {};

      const isCompletedStatus = 
        selectedStatusLabel.toLowerCase().includes('completed') || 
        selectedStatusLabel.toLowerCase().includes('finish') || 
        selectedStatusLabel.toLowerCase().includes('done');

      if (isCompletedStatus) {
        setNoticeDismissed(false);
        setScoringNotice(formatKpiPointsNotice(updatedKpi, updatedKpiType, work.assigned_kpi_points || points, isAssignee));
      } else {
        setScoringNotice(null);
      }
    } catch (err) {
      console.error("Failed to update status", err);
      setSelectedStatusOverride(null);
      alert(err?.response?.data?.message || err?.message || "Failed to update task status");
    } finally {
      setStatusUpdating(false);
      setShowCompleteConfirmModal(false);
      setPendingStatusOption(null);
      setTimeout(() => {
        setSelectedStatusOverride(null);
      }, 600);
    }
  };

  const handleStatusChange = (selectedOption) => {
    if (!selectedOption || statusUpdating || !canEditStatus) return;
    const selectedStatusLabel = selectedOption.label || "";
    const isCompletedStatus = 
      selectedStatusLabel.toLowerCase().includes('completed') || 
      selectedStatusLabel.toLowerCase().includes('finish') || 
      selectedStatusLabel.toLowerCase().includes('done');

    if (isCompletedStatus) {
      setPendingStatusOption(selectedOption);
      setShowCompleteConfirmModal(true);
    } else {
      executeStatusChange(selectedOption);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!commentText.trim() || submittingComment) return;
    setSubmittingComment(true);
    try {
      await dispatch.kpi.addComment({
        task_id: work._id || id,
        comments: commentText.trim(),
        is_reply: false
      });
      setCommentText("");
      dispatch.kpi.getTaskComments({ task_id: id });
      dispatch.kpi.getMyWorkDetail({ id, hideLoading: true });
      dispatch.kpi.getTaskHistory(id);
    } catch (err) {
      console.error("Failed to post comment", err);
      alert(err?.response?.data?.message || "Failed to post comment");
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleReplySubmit = async (e, parentCmt) => {
    e.preventDefault();
    const parentId = typeof parentCmt === 'object' ? (parentCmt?._id || parentCmt?.id) : parentCmt;
    const parentText = typeof parentCmt === 'object' ? (parentCmt?.comment || parentCmt?.comments || parentCmt?.text || parentCmt?.content || "") : "";
    if (!replyText.trim() || submittingReply || !parentId) return;
    setSubmittingReply(true);
    try {
      await dispatch.kpi.addComment({
        task_id: work._id || id,
        comment_id: parentId,
        parent_comment_id: parentId,
        parent_comment: parentText,
        reply: replyText.trim(),
        comment: replyText.trim(),
        is_reply: true
      });
      setReplyText("");
      setReplyingCommentId(null);
      dispatch.kpi.getTaskComments({ task_id: id });
      dispatch.kpi.getMyWorkDetail({ id, hideLoading: true });
      dispatch.kpi.getTaskHistory(id);
    } catch (err) {
      console.error("Failed to post reply", err);
      alert(err?.response?.data?.message || "Failed to post reply");
    } finally {
      setSubmittingReply(false);
    }
  };

  const resolveUserObj = (user) => {
    if (!user) return null;
    if (typeof user === 'object') return user;
    
    const currentUserId = userInfoData?._id || userInfoData?.id;
    if (currentUserId && String(user) === String(currentUserId)) {
      return userInfoData;
    }

    const allKnownUsers = [
      work?.owner,
      work?.created_by,
      ...(Array.isArray(work?.collaborators) ? work.collaborators : []),
      ...(Array.isArray(eligibleUsers) ? eligibleUsers : [])
    ].filter(Boolean);

    const matched = allKnownUsers.find(u => String(u._id || u.id) === String(user));
    return matched || user;
  };

  const getUserInitials = (user) => {
    const resolved = resolveUserObj(user);
    if (!resolved) return "?";
    if (typeof resolved === 'string') return resolved.substring(0, 2).toUpperCase();
    const first = resolved.first_name || resolved.name || "";
    const last = resolved.last_name || "";
    if (first || last) return ((first[0] || "") + (last[0] || "")).toUpperCase();
    return "?";
  };

  const getUserFullName = (user) => {
    const resolved = resolveUserObj(user);
    if (!resolved) return "N/A";
    if (typeof resolved === 'string') return resolved;
    if (resolved.first_name || resolved.last_name) return `${resolved.first_name || ''} ${resolved.last_name || ''}`.trim();
    return resolved.name || resolved.email || "N/A";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric"
      });
    } catch (e) {
      return String(dateStr);
    }
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return String(dateStr);

    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const getAvatarBgColor = (initialsStr) => {
    if (!initialsStr) return "bg-[#047857]";
    const s = String(initialsStr).toUpperCase();
    if (s.includes("JD")) return "bg-[#1d4ed8]";
    if (s.includes("AF")) return "bg-[#b45309]";
    if (s.includes("SR")) return "bg-[#047857]";
    const charCode = (s.charCodeAt(0) || 0) + (s.charCodeAt(1) || 0);
    const colors = ["bg-[#1d4ed8]", "bg-[#b45309]", "bg-[#047857]", "bg-[#6558e8]", "bg-[#c73838]"];
    return colors[charCode % colors.length];
  };

  const getUserRole = (user) => {
    if (!user || typeof user !== 'object') return "";
    return user.role || user.title || user.designation || user.job_title || user.role_name || "";
  };

  const renderCommentContent = (content) => {
    if (!content) return null;
    const parts = content.split(/(@[A-Za-z0-9_\s]+?(?=\s[a-z]|\s[A-Z]|$|\.|\,))/g);
    return parts.map((part, i) => {
      if (part.startsWith("@")) {
        return (
          <span key={i} className="font-semibold text-slate-900">
            {part}
          </span>
        );
      }
      return part;
    });
  };

  const passedWork = location.state?.work || location.state?.item || location.state?.task;

  // Verify that redux workDetail actually belongs to current route id (prevents stale redux data from previous task)
  const detailMatchesId = Boolean(
    workDetail &&
    id &&
    String(workDetail?._id || workDetail?.id || workDetail?.data?._id || workDetail?.data?.id || workDetail?.work?._id || workDetail?.task?._id || "").toLowerCase() === String(id).toLowerCase()
  );

  const rawData = detailMatchesId ? (workDetail?.data || workDetail) : null;
  const apiWork =
    rawData?.work ||
    rawData?.task ||
    rawData?.detail ||
    (rawData?.task_name || rawData?.title || rawData?.name || rawData?._id ? rawData : null);

  const work = apiWork || passedWork || (detailMatchesId ? workDetail : {}) || {};
  const kpiInfo = (detailMatchesId ? (workDetail?.kpi || rawData?.kpi) : null) || work?.kpi || {};
  const kpiTypeInfo = work.kpi_type || (detailMatchesId ? (workDetail?.kpi_type || rawData?.kpi_type) : null) || {};

  const loggedInUser = userInfoData || {};
  const currentUserId = String(loggedInUser._id || loggedInUser.id || "");
  const currentUserEmail = (loggedInUser.email || "").toLowerCase().trim();

  // Check if current logged in user is task assignee
  const isAssignee = React.useMemo(() => {
    if (!currentUserId && !currentUserEmail) return false;

    if (Array.isArray(work.assigned_to) && work.assigned_to.length > 0) {
      const match = work.assigned_to.some((u) => {
        const uid = String(typeof u === "object" ? u._id || u.id || "" : u);
        const uemail = typeof u === "object" && u.email ? u.email.toLowerCase().trim() : "";
        return (currentUserId && uid === currentUserId) || (currentUserEmail && uemail === currentUserEmail);
      });
      if (match) return true;
    }

    const singleAssignee = work.assignee || work.assigned_user || work.assigned_to;
    if (singleAssignee && !Array.isArray(singleAssignee)) {
      const uid = String(typeof singleAssignee === "object" ? singleAssignee._id || singleAssignee.id || "" : singleAssignee);
      const uemail = typeof singleAssignee === "object" && singleAssignee.email ? singleAssignee.email.toLowerCase().trim() : "";
      if ((currentUserId && uid === currentUserId) || (currentUserEmail && uemail === currentUserEmail)) {
        return true;
      }
    }

    return false;
  }, [work, currentUserId, currentUserEmail]);

  const workProjectId = work?.project?._id || work?.project_id || work?.project_details?._id || (typeof work?.project === 'string' ? work.project : null);

  const initialStatus = work.task_status?.task_status || work.status_name || work.status || "";
  const initialPointsVal = work.assigned_kpi_points ?? work.kpi_points ?? work.points ?? work.score ?? work.kpi_type?.score ?? kpiInfo.assigned_points ?? kpiInfo.earned_points;
  const initialPoints = initialPointsVal !== undefined && initialPointsVal !== null ? initialPointsVal : 0;
  const initialStatusUpper = String(initialStatus).toUpperCase();

  useEffect(() => {
    if (workProjectId) {
      dispatch.kpi.getEligibleUsers({ project_id: workProjectId, limit: 100 });
    } else if (work && Object.keys(work).length > 0) {
      dispatch.kpi.setEligibleUsers([]);
    }
  }, [dispatch, workProjectId]);

  useEffect(() => {
    const isCompletedStatus = 
      initialStatusUpper.includes('FINISHED') || 
      initialStatusUpper.includes('COMPLETED') || 
      initialStatusUpper.includes('DONE');

    if (isCompletedStatus && kpiInfo && (kpiInfo.earned_points !== undefined || kpiInfo.assigned_points !== undefined || kpiInfo.is_late_submission !== undefined) && !noticeDismissed && !scoringNotice) {
      setScoringNotice(formatKpiPointsNotice(kpiInfo, kpiTypeInfo, initialPoints, isAssignee));
    } else if (!isCompletedStatus) {
      setScoringNotice(null);
    }
  }, [kpiInfo, kpiTypeInfo, initialStatusUpper, initialPoints, isAssignee, noticeDismissed, scoringNotice]);

  if (loading && !apiWork && !passedWork && !fetchError && !workDetail?.errorStatus) {
    return (
      <div className="w-full">
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <div className="w-10 h-10 border-4 border-slate-200 border-t-slate-900 rounded-full animate-spin mb-4"></div>
          <p className="text-sm font-medium text-slate-500">Loading work details...</p>
        </div>
      </div>
    );
  }

  const isForbiddenOrNotFound = 
    fetchError?.status === 403 || 
    fetchError?.status === 404 || 
    workDetail?.errorStatus === 403 || 
    workDetail?.errorStatus === 404 ||
    (!loading && !apiWork && !passedWork);

  if (isForbiddenOrNotFound) {
    return (
      <div className="w-full">
        {/* Breadcrumb Links */}
        <div className="flex items-center gap-2 text-xs font-semibold text-slate-400 mb-6">
          <Link to="/kpi/dashboard" className="text-slate-500 hover:text-slate-900 transition-colors !no-underline">
            Dashboard
          </Link>
          <span className="text-slate-300">/</span>
          <Link to="/kpi/works" className="text-slate-500 hover:text-slate-900 transition-colors !no-underline">
            Works
          </Link>
          <span className="text-slate-300">/</span>
          <span className="text-slate-900 font-bold">Page Not Found</span>
        </div>

        {/* Simple Centered Card */}
        <div className="flex items-center justify-center min-h-[500px]">
          <div className="bg-white rounded-3xl border border-slate-200/80 p-10 sm:p-12 text-center max-w-lg w-full shadow-xs">
            <div className="w-14 h-14 rounded-2xl bg-rose-50 border border-rose-100 text-rose-500 flex items-center justify-center mx-auto mb-5">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
            </div>
            
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Page Not Found</h2>
            <p className="text-sm text-slate-500 mb-8 max-w-sm mx-auto leading-relaxed">
              You do not have access to this project or work item, or the requested details do not exist.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link 
                to="/kpi/works" 
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold transition-all shadow-xs cursor-pointer !no-underline"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="19" y1="12" x2="5" y2="12"></line>
                  <polyline points="12 19 5 12 12 5"></polyline>
                </svg>
                <span>Back to Works</span>
              </Link>
              <Link 
                to="/kpi/dashboard" 
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 border border-slate-200 text-slate-700 bg-white hover:bg-slate-50 rounded-xl text-xs font-bold transition-all shadow-2xs cursor-pointer !no-underline"
              >
                Go to Dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const attachmentsList = workDetail?.attachments || work.attachments || rawData?.attachments || [];
  const rawComments = (taskComments && taskComments.length > 0) ? taskComments : (work.comments || workDetail?.comments || rawData?.comments || rawData?.comments_data || workDetail?.comments_data || []);
  const commentsList = rawComments.map(item => (item.comments && typeof item.comments === 'object' ? item.comments : item));
  const rawHistory = (taskHistory && taskHistory.length > 0) ? taskHistory : (work.activity_log || workDetail?.activity_log || rawData?.activity_log || []);
  const activityLogList = Array.isArray(rawHistory) ? rawHistory : [];

  const parseHistoryItem = (item) => {
    if (!item) return null;

    // 1. Resolve User Name
    let userName = "";
    const actingUser = item.acting_user || item.updated_by || item.user || item.created_by;

    if (actingUser && typeof actingUser === "object") {
      userName = `${actingUser.first_name || ""} ${actingUser.last_name || ""}`.trim() || actingUser.name || actingUser.email || "";
    } else if (actingUser && typeof actingUser === "string") {
      const allKnownUsers = [
        work?.owner,
        work?.created_by,
        ...(Array.isArray(work?.collaborators) ? work.collaborators : []),
        ...(Array.isArray(eligibleUsers) ? eligibleUsers : [])
      ].filter(Boolean);

      const matchedUser = allKnownUsers.find(u => String(u._id || u.id) === String(actingUser));
      if (matchedUser) {
        userName = `${matchedUser.first_name || ""} ${matchedUser.last_name || ""}`.trim() || matchedUser.name || matchedUser.email || "";
      }
    }

    if (!userName && work?.owner) {
      userName = `${work.owner.first_name || ""} ${work.owner.last_name || ""}`.trim();
    }
    if (!userName && item.user_name) userName = item.user_name;
    if (!userName && item.name) userName = item.name;
    if (!userName) userName = "User";

    // 2. Resolve Action Message based on event_type and values
    let actionText = "";
    const rawEventType = item.event_type || item.action_type || item.type || "";
    const eventType = rawEventType.toUpperCase();

    const parseValueObj = (val) => {
      if (!val) return {};
      if (typeof val === 'object') return val;
      if (typeof val === 'string') {
        const trimmed = val.trim();
        if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
          try {
            const parsed = JSON.parse(trimmed);
            if (parsed && typeof parsed === 'object') return parsed;
          } catch (e) {}
        }
      }
      return {};
    };

    const prevValObj = parseValueObj(item.previous_value);
    const newValObj = parseValueObj(item.new_value);

    const formatVal = (val) => {
      if (val === undefined || val === null) return "";
      let objVal = val;
      if (typeof val === 'string' && val.trim().startsWith('{') && val.trim().endsWith('}')) {
        try { objVal = JSON.parse(val); } catch (e) {}
      }
      if (typeof objVal === 'object' && objVal !== null) {
        const extracted = objVal.name || objVal.title || objVal.status_name || objVal.task_status || objVal.task_priority || objVal.priority || objVal.group_name || objVal.group_id;
        if (extracted !== undefined && extracted !== null) return formatVal(extracted);
      }
      const str = String(objVal).trim();
      if (/^\d{4}-\d{2}-\d{2}/.test(str) || (str.includes("T") && str.includes("Z"))) {
        const d = new Date(str);
        if (!isNaN(d.getTime())) {
          return d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
        }
      }
      return str.replace(/"/g, "");
    };

    const formattedEventType = rawEventType
      ? rawEventType.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())
      : "Activity";

    const getGroupVal = (val, valDetails) => {
      if (valDetails && valDetails.group_id) {
        const detailVal = valDetails.group_id;
        if (typeof detailVal === "object" && detailVal !== null) {
          if (detailVal.group_name || detailVal.name || detailVal.title) {
            return String(detailVal.group_name || detailVal.name || detailVal.title).trim();
          }
        }
      }
      if (val === undefined || val === null) return null;
      if (typeof val === "object" && val !== null) {
        const extracted = val.group_name || val.name || val.title || val.group_id || val.group;
        if (extracted === undefined || extracted === null) return null;
        return getGroupVal(extracted);
      }
      const str = String(val).trim();
      if (str === "" || str.toLowerCase() === "null" || str.toLowerCase() === "undefined") return null;
      return str.replace(/"/g, "");
    };

    const getAssigneeName = (val, valDetails) => {
      const target = (valDetails && (valDetails.assigned_to || valDetails.assignee)) || val;
      if (!target) return "";
      if (Array.isArray(target)) {
        return target
          .map((u) => {
            if (typeof u === "object" && u !== null) {
              return u.full_name || `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.name || u.email || "";
            }
            return String(u);
          })
          .filter(Boolean)
          .join(", ")
          .replace(/"/g, "");
      }
      if (typeof target === "object" && target !== null) {
        return (target.full_name || `${target.first_name || ""} ${target.last_name || ""}`.trim() || target.name || target.email || "").replace(/"/g, "");
      }
      return String(target).replace(/"/g, "");
    };

    if (eventType === "DESCRIPTION_CHANGE" || eventType === "TASK_DESCRIPTION_CHANGE" || "task_description" in prevValObj || "task_description" in newValObj || "description" in prevValObj || "description" in newValObj) {
      actionText = "changed description";
    } else if (eventType === "ASSIGNMENT_CHANGE" || eventType === "ASSIGNEE_CHANGE" || "assigned_to" in prevValObj || "assigned_to" in newValObj || "assignee" in prevValObj || "assignee" in newValObj) {
      const prevAssignee = getAssigneeName(prevValObj.assigned_to || prevValObj.assignee, item.previous_value_details);
      const newAssignee = getAssigneeName(newValObj.assigned_to || newValObj.assignee, item.new_value_details);
      if (prevAssignee && newAssignee && prevAssignee !== newAssignee) {
        actionText = `changed assignee from ${prevAssignee} to ${newAssignee}`;
      } else if (newAssignee) {
        actionText = `assigned to ${newAssignee}`;
      } else if (prevAssignee) {
        actionText = `unassigned from ${prevAssignee}`;
      } else {
        actionText = "updated assignee";
      }
    } else if (
      eventType === "DUE_DATE_CHANGE" ||
      eventType === "DUE_DATE_UPDATED" ||
      eventType === "TASK_DUE_DATE_CHANGE" ||
      eventType === "DUE_DATE" ||
      eventType.includes("DUE_DATE") ||
      eventType.includes("END_DATE") ||
      "task_end_date" in prevValObj ||
      "task_end_date" in newValObj ||
      "due_date" in prevValObj ||
      "due_date" in newValObj
    ) {
      const prevDate = formatVal(prevValObj.task_end_date || prevValObj.due_date || (typeof item.previous_value !== 'object' && !String(item.previous_value).startsWith('{') ? item.previous_value : ''));
      const newDate = formatVal(newValObj.task_end_date || newValObj.due_date || (typeof item.new_value !== 'object' && !String(item.new_value).startsWith('{') ? item.new_value : ''));
      if (prevDate && newDate && prevDate !== newDate) {
        actionText = `changed due date from ${prevDate} to ${newDate}`;
      } else if (newDate) {
        actionText = `set due date to ${newDate}`;
      } else {
        actionText = `updated due date`;
      }
    } else if (
      eventType.includes("GROUP") ||
      "group_id" in prevValObj ||
      "group_id" in newValObj ||
      "group_name" in prevValObj ||
      "group_name" in newValObj ||
      "group" in prevValObj ||
      "group" in newValObj
    ) {
      const prevGroup = getGroupVal(prevValObj.group_id ?? prevValObj.group_name ?? prevValObj.group ?? (typeof item.previous_value !== 'object' && !String(item.previous_value).startsWith('{') ? item.previous_value : null), item.previous_value_details);
      const newGroup = getGroupVal(newValObj.group_id ?? newValObj.group_name ?? newValObj.group ?? (typeof item.new_value !== 'object' && !String(item.new_value).startsWith('{') ? item.new_value : null), item.new_value_details);

      if (!prevGroup && newGroup) {
        actionText = `moved to group ${newGroup}`;
      } else if (prevGroup && newGroup && prevGroup !== newGroup) {
        actionText = `moved from group ${prevGroup} to ${newGroup}`;
      } else if (prevGroup && !newGroup) {
        actionText = `removed from group ${prevGroup}`;
      } else if (newGroup) {
        actionText = `moved to group ${newGroup}`;
      }
    } else if (eventType.includes("COMMENT") || (item.action && String(item.action).toUpperCase().includes("COMMENT")) || newValObj.comment_text || newValObj.comment) {
      const commentTxt = item.comment || item.message || item.text || item.content || newValObj.comment_text || newValObj.comment || "";
      const cleanComment = String(commentTxt).replace(/"/g, "");
      actionText = cleanComment ? `added a comment: ${cleanComment}` : "added a comment";
    } else if (eventType === "CREATE" || eventType === "WORK_CREATE" || eventType === "TASK_CREATE") {
      actionText = "created this work";
    } else if (eventType === "STATUS_CHANGE" || (prevValObj.status_name || newValObj.status_name || prevValObj.task_status || newValObj.task_status || item.previous_value_details?.task_status || item.new_value_details?.task_status || "status_name" in prevValObj || "status_name" in newValObj)) {
      const prevStatus = (prevValObj.status_name || prevValObj.task_status?.status_name || item.previous_value_details?.task_status?.status_name || "").replace(/"/g, "");
      const newStatus = (newValObj.status_name || newValObj.task_status?.status_name || item.new_value_details?.task_status?.status_name || "").replace(/"/g, "");
      if (prevStatus && newStatus && prevStatus !== newStatus) {
        actionText = `changed status: ${prevStatus} - ${newStatus}`;
      } else if (newStatus) {
        actionText = `updated status to ${newStatus}`;
      } else {
        actionText = `updated ${formattedEventType}`;
      }
    } else if (eventType === "PRIORITY_CHANGE" || (prevValObj.task_priority || newValObj.task_priority || prevValObj.priority || newValObj.priority || "task_priority" in prevValObj || "task_priority" in newValObj)) {
      const prevPrio = (prevValObj.task_priority || prevValObj.priority || "").replace(/"/g, "");
      const newPrio = (newValObj.task_priority || newValObj.priority || "").replace(/"/g, "");
      if (prevPrio && newPrio && prevPrio !== newPrio) {
        actionText = `changed priority from ${prevPrio} to ${newPrio}`;
      } else if (newPrio) {
        actionText = `updated priority to ${newPrio}`;
      } else {
        actionText = `updated ${formattedEventType}`;
      }
    } else if (item.action || item.description || item.message) {
      const rawText = item.action || item.description || item.message;
      actionText = String(rawText).replace(/\s*(?:→|->|=>)\s*/g, " - ");
    } else if (item.previous_value && item.new_value) {
      const oldVal = formatVal(item.previous_value);
      const newVal = formatVal(item.new_value);
      if (oldVal && newVal) {
        actionText = `updated ${formattedEventType}: ${oldVal} - ${newVal}`;
      } else {
        actionText = `updated ${formattedEventType}`;
      }
    } else {
      actionText = `performed ${formattedEventType}`;
    }

    // Clean up any remaining arrows, quotes, and format ISO dates in text
    actionText = actionText.replace(/\s*(?:→|->|=>)\s*/g, " - ");
    actionText = actionText.replace(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z?/g, (match) => {
      const d = new Date(match);
      return !isNaN(d.getTime()) ? d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }) : match;
    });
    actionText = actionText.replace(/"/g, "");

    // 3. Resolve Relative Time
    const timeAgo = item.time || formatTimeAgo(item.createdAt || item.created_at || item.updatedAt || item.date);

    return {
      userName,
      actionText,
      timeAgo
    };
  };

  const displayId = work.work_id || work.human_readable_id || (work._id ? `KPI-${work._id.substring(work._id.length - 4).toUpperCase()}` : id || "N/A");
  const title = work.task_name || work.title || work.name || work.task_title || work.work_name || work.summary || "Untitled Work";
  const description = work.task_description || work.description || work.details || work.content || null;
  const status = work.task_status?.task_status || work.status_name || work.status || kpiInfo.review_status || "NEW";
  const priority = work.task_priority || work.priority || "Medium";
  const typeName = work.kpi_type?.name || work.kpi_type_name || work.task_type_name || work.type_name || (typeof work.task_type === 'object' ? work.task_type?.name || work.task_type?.type : work.task_type) || kpiInfo.type || "Task";

  const pointsVal = work.assigned_kpi_points ?? work.kpi_points ?? work.points ?? work.score ?? work.kpi_type?.score ?? kpiInfo.assigned_points ?? kpiInfo.earned_points;
  const points = pointsVal !== undefined && pointsVal !== null ? pointsVal : 0;

  const dueDate = work.task_end_date || work.due_date || work.next_due_date || work.end_date || work.due || work.next_due || work.task_due_date;
  const createdDate = work.created_at || work.createdAt || work.task_start_date || work.start_date || work.createdDate || work.date;
  const projectName = (typeof work.project === 'object' ? (work.project?.name || work.project?.project_name || work.project?.title) : null) || work.project_name || work.projectName || (typeof work.project === 'string' ? work.project : null) || work.project_details?.name || work.project_details?.project_name || "N/A";

  const owner = work.owner || work.assigned_by || work.created_by || work.user || work.assignee || (Array.isArray(work.assigned_to) && work.assigned_to.length > 0 ? work.assigned_to[0] : null);
  const collaborators = Array.isArray(work.collaborators)
    ? work.collaborators
    : (Array.isArray(work.assigned_to)
      ? work.assigned_to
      : (work.collaborators ? [work.collaborators] : (work.assigned_to ? [work.assigned_to] : [])));

  const isEditable = work.is_editable !== undefined ? Boolean(work.is_editable) : true;
  const canEditStatus = work.can_edit_status !== undefined ? Boolean(work.can_edit_status) : true;

  const statusUpper = String(status).toUpperCase();
  const priorityLower = String(priority).toLowerCase();

  const isHighPriority = priorityLower === 'high';
  const isLowPriority = priorityLower === 'low';

  const statusOptions = (statusList && statusList.length > 0)
    ? statusList.map(s => ({ value: s._id, label: s.task_status }))
    : [
        { value: 'todo', label: 'Todo' },
        { value: 'in_progress', label: 'In progress' },
        { value: 'waiting', label: 'Waiting' },
        { value: 'review', label: 'Review' },
        { value: 'completed', label: 'Completed' }
      ];

  const currentStatusObj = selectedStatusOverride || statusOptions.find(
    opt => {
      const currentStatusId = work.task_status?._id || (typeof work.task_status === 'string' ? work.task_status : null) || work.task_status_id || work.status_id;
      if (currentStatusId && String(opt.value) === String(currentStatusId)) return true;
      
      const currentStatusName = (
        work.task_status?.task_status || 
        work.status_name || 
        work.status || 
        status || 
        ""
      ).toLowerCase();
      return opt.label.toLowerCase() === currentStatusName;
    }
  ) || { value: status, label: status };

  const timelineSteps = (statusList && statusList.length > 0)
    ? statusList.map(s => s.task_status || s.status_name || s.name || s)
    : ["Todo", "In progress", "Waiting", "Review", "Completed"];

  const getActiveStepIndex = () => {
    if (!timelineSteps || timelineSteps.length === 0) return 0;
    
    const currentStatusId = work.task_status?._id || (typeof work.task_status === 'string' ? work.task_status : null) || work.task_status_id || work.status_id;
    const currentStatusName = (
      work.task_status?.task_status || 
      work.status_name || 
      work.status || 
      (currentStatusObj ? currentStatusObj.label : "")
    ).toLowerCase();

    if (statusList && statusList.length > 0 && currentStatusId) {
      const foundIdx = statusList.findIndex(s => String(s._id) === String(currentStatusId));
      if (foundIdx !== -1) return foundIdx;
    }

    const foundLabelIdx = timelineSteps.findIndex(step => String(step).toLowerCase() === currentStatusName);
    if (foundLabelIdx !== -1) return foundLabelIdx;

    const st = currentStatusName.toUpperCase();
    if (st.includes("NEW") || st.includes("TODO")) {
      const idx = timelineSteps.findIndex(s => String(s).toUpperCase().includes("TODO") || String(s).toUpperCase().includes("NEW"));
      return idx !== -1 ? idx : 0;
    }
    if (st.includes("PROGRESS") || st.includes("ACTIVE") || st.includes("DOING")) {
      const idx = timelineSteps.findIndex(s => String(s).toUpperCase().includes("PROGRESS") || String(s).toUpperCase().includes("ACTIVE"));
      return idx !== -1 ? idx : 1;
    }
    if (st.includes("WAIT") || st.includes("HOLD") || st.includes("PAUSE")) {
      const idx = timelineSteps.findIndex(s => String(s).toUpperCase().includes("WAIT") || String(s).toUpperCase().includes("HOLD"));
      return idx !== -1 ? idx : 2;
    }
    if (st.includes("REVIEW") || st.includes("SUBMIT")) {
      const idx = timelineSteps.findIndex(s => String(s).toUpperCase().includes("REVIEW"));
      return idx !== -1 ? idx : 3;
    }
    if (st.includes("FINISHED") || st.includes("COMPLETED") || st.includes("DONE")) {
      const idx = timelineSteps.findIndex(s => String(s).toUpperCase().includes("COMPLET") || String(s).toUpperCase().includes("DONE") || String(s).toUpperCase().includes("FINISH"));
      return idx !== -1 ? idx : 4;
    }

    return 0;
  };
  const activeStep = getActiveStepIndex();

  const updatedAtAgo = work.updatedAt ? formatTimeAgo(work.updatedAt) : null;

  const isKpiLocked = Boolean(
    kpiInfo.submitted_for_review_at || 
    kpiInfo.earned_points !== undefined ||
    statusUpper.includes("REVIEW") || 
    statusUpper.includes("FINISHED") || 
    statusUpper.includes("COMPLETED")
  );

  const userOptions = (eligibleUsers || []).map(u => ({
    value: u._id || u.id,
    label: u.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.name || u.email
  }));

  const openEditModal = () => {
    if (!isEditable) return;
    if (workProjectId) {
      dispatch.kpi.getEligibleUsers({ project_id: workProjectId, limit: 100 });
    } else {
      dispatch.kpi.setEligibleUsers([]);
    }
    const workDueDate = dueDate ? moment(dueDate).format("YYYY-MM-DD") : "";
    const kpiTypeId = work.task_kpi_type_id || work.kpi_type?._id || kpiInfo.kpi_type_id || "";
    const currentStatusId = work.task_status?._id || (typeof work.task_status === 'string' ? work.task_status : null) || work.task_status_id || "";
    const assignedToIds = Array.isArray(work.assigned_to) 
      ? work.assigned_to.map(u => (typeof u === 'object' ? u._id : u))
      : (collaborators ? collaborators.map(u => (typeof u === 'object' ? u._id : u)) : []);

    setEditForm({
      task_name: title || "",
      task_description: description || "",
      task_priority: priority || "Medium",
      task_due_date: workDueDate,
      assigned_kpi_points: points !== undefined ? points : "",
      task_kpi_type_id: kpiTypeId,
      task_status: currentStatusId || (currentStatusObj ? currentStatusObj.value : ""),
      assigned_to: assignedToIds
    });
    setShowEditModal(true);
  };

  const handleEditTypeChange = (e) => {
    const typeId = e.target.value;
    const selected = (taskTypes || []).find(t => t._id === typeId);
    setEditForm(prev => ({
      ...prev,
      task_kpi_type_id: typeId,
      assigned_kpi_points: selected && selected.score !== undefined ? selected.score : prev.assigned_kpi_points
    }));
  };

  const handleEditAssigneeChange = (selectedOption) => {
    setEditForm(prev => ({
      ...prev,
      assigned_to: selectedOption ? [selectedOption.value] : []
    }));
  };

  const handleEditFormSubmit = async (e) => {
    e.preventDefault();
    if (!isEditable) return;
    if (!editForm.task_name || !editForm.task_due_date) {
      alert("Please fill in required fields (Title and Due Date).");
      return;
    }

    setEditSubmitting(true);
    try {
      const payload = {
        task_id: work._id || id,
        project_id: work.project?._id || null,
        task_name: editForm.task_name,
        task_description: editForm.task_description,
        task_priority: editForm.task_priority,
        task_end_date: formatEndDateIso(editForm.task_due_date),
        assigned_to: editForm.assigned_to,
        task_status: editForm.task_status || undefined
      };

      if (!isKpiLocked) {
        if (editForm.task_kpi_type_id) payload.task_kpi_type_id = editForm.task_kpi_type_id;
        if (editForm.assigned_kpi_points !== "") payload.assigned_kpi_points = Number(editForm.assigned_kpi_points);
      }

      await dispatch.kpi.editWork(payload);
      setShowEditModal(false);

      const updatedDetailRes = await dispatch.kpi.getMyWorkDetail({ id, hideLoading: true });
      dispatch.kpi.getTaskHistory(id);

      const selectedStatusLabel = statusOptions.find(s => String(s.value) === String(editForm.task_status))?.label || "";
      const isCompletedStatus = 
        selectedStatusLabel.toLowerCase().includes("completed") || 
        selectedStatusLabel.toLowerCase().includes("finish") || 
        selectedStatusLabel.toLowerCase().includes("done");

      if (isCompletedStatus) {
        const updatedKpi = updatedDetailRes?.data?.kpi || updatedDetailRes?.kpi || {};
        const updatedKpiType = updatedDetailRes?.data?.kpi_type || updatedDetailRes?.kpi_type || work?.kpi_type || {};
        setNoticeDismissed(false);
        setScoringNotice(formatKpiPointsNotice(updatedKpi, updatedKpiType, payload.assigned_kpi_points || work.assigned_kpi_points || points, isAssignee));
      } else {
        setScoringNotice({
          type: 'success',
          message: "Work item updated successfully."
        });
      }
    } catch (err) {
      console.error("Failed to edit work", err);
      const errMsg = err?.response?.data?.message || err?.message || "Failed to update work.";
      if (errMsg.includes("KPI_CONFIGURATION_LOCKED")) {
        alert("KPI configuration is locked because this task has already entered Review.");
      } else {
        alert(errMsg);
      }
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDeleteWork = async () => {
    if (deletingWork || !isEditable) return;
    setDeletingWork(true);
    try {
      await dispatch.kpi.softDeleteWork(work._id || id);
      dispatch.kpi.getMyWorks({ status: "all" });
      dispatch.kpi.getKPIProjects({ hideLoading: true });
      setShowDeleteConfirm(false);
      navigate("/kpi/works");
    } catch (err) {
      console.error("Failed to delete work item", err);
      alert(err?.response?.data?.message || err?.message || "Failed to delete work item.");
    } finally {
      setDeletingWork(false);
    }
  };

  return (
    <div className="w-full">
      {/* Breadcrumbs */}
      <div className="flex items-center gap-2 text-xs font-medium text-slate-500 mb-4">
        <Link to="/kpi/works" className="hover:text-slate-900 transition-colors">Works</Link>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
        <span className="text-slate-900">{displayId}</span>
      </div>

      {scoringNotice && (
        <div className={`mb-6 p-4 rounded-xl text-sm font-medium flex items-center justify-between shadow-xs transition-all ${
          scoringNotice.type === 'warning' ? 'bg-rose-50 text-rose-800 border border-rose-200' :
          scoringNotice.type === 'success' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' :
          'bg-slate-100 text-slate-800 border border-slate-200'
        }`}>
          <span>{scoringNotice.message}</span>
          <button onClick={() => { setScoringNotice(null); setNoticeDismissed(true); }} className="text-xs text-slate-400 hover:text-slate-600 font-bold ml-4">✕</button>
        </div>
      )}

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            
            <span className={`flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wide ${statusUpper.includes('FINISHED') || statusUpper.includes('COMPLETED') ? 'bg-emerald-50 text-emerald-700' : statusUpper.includes('DELAY') ? 'bg-rose-50 text-rose-700' : 'bg-amber-50 text-amber-700'}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${statusUpper.includes('FINISHED') || statusUpper.includes('COMPLETED') ? 'bg-emerald-500' : statusUpper.includes('DELAY') ? 'bg-rose-500' : 'bg-amber-500'}`}></span>
              {status}
            </span>
            {(() => {
              const typeColor = getKpiTypeColor(typeName);
              return (
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wide ${typeColor.badgeBg}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${typeColor.bg}`}></span>
                  {typeName}
                </span>
              );
            })()}
            <span className={`flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wide ${isHighPriority ? 'bg-rose-50 text-rose-600' : isLowPriority ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${isHighPriority ? 'bg-rose-500' : isLowPriority ? 'bg-emerald-500' : 'bg-amber-500'}`}></span>
              {priority}
            </span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-1">{title}</h1>
          <p className="text-slate-500 text-sm">
            Created {createdDate ? formatDate(createdDate) : 'N/A'}{owner ? ` by ${getUserFullName(owner)}` : ''}{updatedAtAgo ? ` · last update ${updatedAtAgo}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {canEditStatus && (
            <div className="min-w-[170px]">
              <Select
                options={statusOptions}
                value={currentStatusObj}
                onChange={handleStatusChange}
                isDisabled={statusUpdating}
                isLoading={statusUpdating}
                className="text-xs font-semibold"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: '#cbd5e1',
                    borderRadius: '0.5rem',
                    minHeight: '38px',
                    backgroundColor: '#0f172a',
                    color: '#ffffff',
                    boxShadow: 'none',
                    '&:hover': {
                      borderColor: '#0f172a'
                    }
                  }),
                  singleValue: (base) => ({
                    ...base,
                    color: '#ffffff',
                    fontWeight: '600'
                  }),
                  menu: (base) => ({
                    ...base,
                    borderRadius: '0.5rem',
                    zIndex: 50
                  }),
                  option: (base, state) => ({
                    ...base,
                    fontSize: '12px',
                    backgroundColor: state.isSelected ? '#0f172a' : state.isFocused ? '#f1f5f9' : '#ffffff',
                    color: state.isSelected ? '#ffffff' : '#1e293b',
                    cursor: 'pointer'
                  })
                }}
              />
            </div>
          )}
          {isEditable && (
            <button 
              type="button"
              onClick={openEditModal}
              className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 transition-colors shadow-2xs cursor-pointer"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
              Edit
            </button>
          )}
          {isEditable && (
            <div className="relative">
              <button 
                type="button"
                onClick={() => setShowActionMenu(!showActionMenu)}
                className="flex items-center justify-center w-10 h-10 border border-slate-200 rounded-lg text-slate-700 bg-white hover:bg-slate-50 transition-colors cursor-pointer shadow-2xs"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="1"></circle><circle cx="19" cy="12" r="1"></circle><circle cx="5" cy="12" r="1"></circle></svg>
              </button>

              {showActionMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowActionMenu(false)}></div>
                  <div className="absolute right-0 mt-2 w-44 bg-white border border-slate-200 rounded-xl shadow-lg z-20 py-1 overflow-hidden animate-in fade-in zoom-in-95 duration-100">
                    <button
                      type="button"
                      onClick={() => {
                        setShowActionMenu(false);
                        setShowDeleteConfirm(true);
                      }}
                      className="w-full px-4 py-2.5 text-left text-xs font-semibold text-rose-600 hover:bg-rose-50 flex items-center gap-2.5 transition-colors cursor-pointer"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        <line x1="10" y1="11" x2="10" y2="17"></line>
                        <line x1="14" y1="11" x2="14" y2="17"></line>
                      </svg>
                      Delete Work
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Main Content */}
        <div className="flex-1 space-y-6">
          
          {/* Status Timeline */}
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6">
            <h2 className="text-base font-semibold text-slate-900 mb-6">Status timeline</h2>
            <div className="flex items-center justify-between w-full px-2">
              {timelineSteps.map((stepLabel, idx) => {
                const isReached = idx <= activeStep;
                const isCurrent = idx === activeStep;
                const isLineGreen = idx < activeStep;

                return (
                  <React.Fragment key={idx}>
                    {/* Node Circle & Label */}
                    <div className="flex flex-col items-center gap-2 z-10 shrink-0">
                      {isReached ? (
                        <div className={`w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center shadow-sm transition-all duration-500 transform ${isCurrent ? 'scale-105 ring-4 ring-emerald-100' : 'scale-100'}`}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        </div>
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-[#f7f6f3] text-slate-700 flex items-center justify-center text-xs font-bold transition-all duration-500 transform scale-95">
                          {idx + 1}
                        </div>
                      )}
                      <span className={`text-xs font-bold transition-colors duration-300 ${isReached ? 'text-slate-900' : 'text-slate-400'}`}>
                        {stepLabel}
                      </span>
                    </div>

                    {/* Connecting Line Segment between nodes */}
                    {idx < timelineSteps.length - 1 && (
                      <div className="flex-1 mx-3 mb-6 relative self-center">
                        <div className="h-[3px] w-full bg-slate-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-emerald-600 rounded-full transition-all duration-500 ease-in-out" 
                            style={{ width: isLineGreen ? '100%' : '0%' }}
                          />
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Description Card */}
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6">
            <h2 className="text-base font-semibold text-slate-900 mb-3">Description</h2>
            {description ? (
              <div className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">
                {description}
              </div>
            ) : (
              <div className="text-sm text-slate-400 italic">No description provided.</div>
            )}
          </div>



          {/* Discussion */}
          {(() => {
            const currentUserInitials = getUserInitials(userInfoData) !== "?" ? getUserInitials(userInfoData) : "SR";
            const currentUserAvatarBg = getAvatarBgColor(currentUserInitials);

            const normalizedList = rawComments.map((item) => {
              if (item && typeof item === "object") {
                return item.comments && typeof item.comments === "object" ? item.comments : item;
              }
              return item;
            }).filter(Boolean);

            const topLevelComments = [];
            const repliesByParentId = {};

            normalizedList.forEach((c) => {
              if (c.is_reply) {
                const parentId = String(c.parent_comment_id || c.parent_id || c.comment_id || "");
                if (!repliesByParentId[parentId]) {
                  repliesByParentId[parentId] = [];
                }
                repliesByParentId[parentId].push(c);
              } else {
                topLevelComments.push(c);
              }
            });

            return (
              <div className="bg-white rounded-2xl border border-slate-200/90 shadow-2xs p-6">
                {/* Header */}
                <div className="pb-4 mb-6 border-b border-slate-200/60">
                  <h2 className="text-base font-bold text-slate-900 tracking-tight">Discussion</h2>
                  <p className="text-xs text-slate-400 font-normal mt-0.5">
                    {topLevelComments.length} {topLevelComments.length === 1 ? 'comment' : 'comments'}
                  </p>
                </div>

                {/* Comments List & Add Comment Input */}
                <div className="space-y-6">
                  {topLevelComments.length > 0 ? (
                    <>
                      {topLevelComments.slice(0, visibleCommentsCount).map((cmt, idx) => {
                        const commentId = cmt._id || cmt.id || idx;
                        const authorObj = cmt.user || cmt.user_id || cmt.author || cmt.created_by;
                        const commentContent = cmt.comment || cmt.comments || cmt.text || cmt.content;
                        const createdAtTime = cmt.created_At || cmt.createdAt;
                        const authorInitials = getUserInitials(authorObj);
                        const avatarBg = getAvatarBgColor(authorInitials);
                        const roleStr = getUserRole(authorObj);

                        const embeddedReplies = Array.isArray(cmt.replies) ? cmt.replies : (cmt.reply ? [cmt.reply] : []);
                        const dynamicReplies = repliesByParentId[String(commentId)] || [];
                        const replyMap = new Map();

                        [...embeddedReplies, ...dynamicReplies].forEach((rep) => {
                          const rObj = rep.comments && typeof rep.comments === 'object' ? rep.comments : rep;
                          const rId = String(rObj._id || rObj.id || Math.random());
                          if (!replyMap.has(rId)) {
                            replyMap.set(rId, rObj);
                          }
                        });
                        const repliesList = Array.from(replyMap.values());

                        return (
                          <div key={commentId} className={`space-y-3 ${idx > 0 ? "pt-5 border-t border-slate-100" : ""}`}>
                            <div className="flex gap-3.5 items-start">
                              {/* User Avatar Circle */}
                              <div className={`w-8 h-8 shrink-0 rounded-full ${avatarBg} text-white flex items-center justify-center text-xs font-bold shadow-2xs mt-0.5`}>
                                {authorInitials}
                              </div>

                              {/* Comment Content Body */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2 mb-1">
                                  <div className="flex items-center gap-1.5 flex-wrap text-xs">
                                    <span className="text-sm font-bold text-slate-900 leading-none">
                                      {getUserFullName(authorObj)}
                                    </span>
                                    {roleStr && (
                                      <span className="text-xs text-slate-400 font-normal">
                                        {roleStr}
                                      </span>
                                    )}
                                    <span className="text-slate-300 font-normal">·</span>
                                    <span className="text-xs text-slate-400 font-normal">
                                      {createdAtTime ? formatTimeAgo(createdAtTime) : 'recently'}
                                    </span>
                                  </div>

                                  <button 
                                    type="button"
                                    onClick={() => {
                                      setReplyingCommentId(replyingCommentId === commentId ? null : commentId);
                                      setReplyText("");
                                    }}
                                    className="text-xs text-slate-400 hover:text-slate-700 font-medium transition-colors cursor-pointer"
                                  >
                                    Reply
                                  </button>
                                </div>

                                <div className="text-sm text-slate-700 leading-relaxed font-normal whitespace-pre-line">
                                  {renderCommentContent(commentContent)}
                                </div>

                                {/* Replies List (Nested under main comment with left border, NO reply option on replies) */}
                                {repliesList.length > 0 && (
                                  <div className="mt-4 space-y-4 pl-4 border-l-2 border-slate-100">
                                    {repliesList.map((rep, rIdx) => {
                                      const replyAuthor = rep.user || rep.user_id || rep.author || rep.created_by;
                                      const replyContent = rep.reply || rep.comment || rep.comments || rep.text;
                                      const replyTime = rep.created_At || rep.createdAt;
                                      const repInitials = getUserInitials(replyAuthor);
                                      const repAvatarBg = getAvatarBgColor(repInitials);
                                      const repRole = getUserRole(replyAuthor);

                                      return (
                                        <div key={rep._id || rep.id || rIdx} className="flex gap-3 items-start">
                                          <div className={`w-7 h-7 shrink-0 rounded-full ${repAvatarBg} text-white flex items-center justify-center text-[10px] font-bold mt-0.5 shadow-2xs`}>
                                            {repInitials}
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-1.5 flex-wrap text-xs mb-0.5">
                                              <span className="text-xs font-bold text-slate-900">{getUserFullName(replyAuthor)}</span>
                                              {repRole && <span className="text-[11px] text-slate-400 font-normal">{repRole}</span>}
                                              <span className="text-slate-300">·</span>
                                              <span className="text-[11px] text-slate-400">{replyTime ? formatTimeAgo(replyTime) : 'recently'}</span>
                                            </div>
                                            <div className="text-xs text-slate-700 leading-relaxed font-normal whitespace-pre-line">
                                              {renderCommentContent(replyContent)}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}

                                {/* Inline Reply Input Form */}
                                {replyingCommentId === commentId && (
                                  <form onSubmit={(e) => handleReplySubmit(e, cmt)} className="mt-3 pl-4 border-l-2 border-slate-900">
                                    <div className="flex gap-2">
                                      <input
                                        type="text"
                                        value={replyText}
                                        onChange={(e) => setReplyText(e.target.value)}
                                        placeholder={`Replying to ${getUserFullName(authorObj)}...`}
                                        className="flex-1 px-3 py-1.5 text-xs border border-slate-200 rounded-xl focus:outline-none focus:border-slate-900 bg-white text-slate-900"
                                        autoFocus
                                      />
                                      <button
                                        type="submit"
                                        disabled={submittingReply || !replyText.trim()}
                                        className="px-3.5 py-1.5 bg-slate-900 text-white rounded-xl text-xs font-bold hover:bg-slate-800 disabled:opacity-50 transition-colors flex items-center gap-1 cursor-pointer"
                                      >
                                        {submittingReply && <div className="w-2.5 h-2.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                                        Reply
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => { setReplyingCommentId(null); setReplyText(""); }}
                                        className="px-2 py-1.5 text-slate-400 hover:text-slate-600 text-xs font-medium cursor-pointer"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </form>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}

                      {/* Pagination Controls */}
                      {topLevelComments.length > 5 && (
                        <div className="flex items-center justify-between pt-3 pb-1 border-t border-slate-100 text-xs font-semibold">
                          <span className="text-slate-400">
                            Showing {Math.min(visibleCommentsCount, topLevelComments.length)} of {topLevelComments.length} comments
                          </span>
                          <div className="flex items-center gap-3">
                            {visibleCommentsCount < topLevelComments.length ? (
                              <>
                                <button
                                  type="button"
                                  onClick={() => setVisibleCommentsCount((prev) => prev + 5)}
                                  className="text-slate-700 hover:text-slate-900 bg-slate-100 hover:bg-slate-200/80 px-3 py-1 rounded-lg transition-colors cursor-pointer"
                                >
                                  Load next 5
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setVisibleCommentsCount(topLevelComments.length)}
                                  className="text-slate-900 hover:underline font-bold transition-colors cursor-pointer"
                                >
                                  View all
                                </button>
                              </>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setVisibleCommentsCount(5)}
                                className="text-slate-500 hover:text-slate-800 transition-colors cursor-pointer"
                              >
                                Show less
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-sm text-slate-400 italic py-1">No comments yet.</div>
                  )}

                  {/* Add Comment Section */}
                  <div className={`flex gap-3.5 items-start ${commentsList.length > 0 ? "pt-5 border-t border-slate-100" : "pt-2"}`}>
                    <div className={`w-8 h-8 shrink-0 rounded-full ${currentUserAvatarBg} text-white flex items-center justify-center text-xs font-bold shadow-2xs mt-1`}>
                      {currentUserInitials}
                    </div>

                    <form onSubmit={handleCommentSubmit} className="flex-1 min-w-0">
                      <div className="border border-slate-200/90 rounded-2xl bg-white p-3 focus-within:border-slate-400 transition-colors shadow-2xs">
                        <textarea 
                          value={commentText}
                          onChange={(e) => setCommentText(e.target.value)}
                          placeholder="Write a comment..." 
                          className="w-full text-sm min-h-[52px] resize-none focus:outline-none bg-transparent text-slate-900 placeholder:text-slate-400 leading-relaxed"
                        ></textarea>

                        <div className="pt-2.5 flex justify-end items-center border-t border-slate-100/70 mt-1">
                          <button 
                            type="submit" 
                            disabled={submittingComment || !commentText.trim()}
                            className="px-5 py-2 bg-slate-900 text-white rounded-xl text-xs font-bold hover:bg-slate-800 disabled:opacity-50 transition-colors flex items-center gap-1.5 shadow-2xs cursor-pointer"
                          >
                            {submittingComment && <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                            Comment
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Activity Log */}
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs p-6">
            <h2 className="text-base font-bold text-slate-900 mb-4 pb-3 border-b border-slate-100">Activity log</h2>
            {activityLogList.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {activityLogList.map((log, idx) => {
                  const parsed = parseHistoryItem(log);
                  if (!parsed) return null;
                  return (
                    <div key={idx} className="flex items-center gap-4 py-3.5 text-sm text-slate-700">
                      <div className="w-24 text-xs font-semibold text-slate-400 shrink-0">{parsed.timeAgo}</div>
                      <div className="flex-1 text-slate-800 leading-normal">
                        <span className="font-bold text-slate-900 mr-1.5">{parsed.userName}</span>
                        <span className="text-slate-700 font-medium">{parsed.actionText}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-xs text-slate-400 italic py-2">No activity recorded yet.</div>
            )}
          </div>

        </div>

        {/* Sidebar */}
        <div className="w-full lg:w-80 shrink-0 space-y-6">
          {/* Details Card */}
          <div className="bg-white rounded-3xl border border-slate-200/80 shadow-xs p-6">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-xs font-semibold text-slate-500">KPI points</span>
                <span className="bg-black text-white text-xs font-bold px-3 py-1 rounded-full shadow-2xs">
                  +{points}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs font-semibold text-slate-500">Due</span>
                <span className="text-xs font-bold text-slate-900">{formatDate(dueDate)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs font-semibold text-slate-500">Project</span>
                <span className="text-xs font-bold text-slate-900">{projectName}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs font-semibold text-slate-500">Created</span>
                <span className="text-xs font-bold text-slate-900">{formatDate(createdDate)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs font-semibold text-slate-500">Owner</span>
                {owner ? (
                  <div className="inline-flex items-center gap-2 bg-[#f7f6f3] hover:bg-[#efeee7] transition-colors pl-1 pr-3 py-1 rounded-full shadow-2xs">
                    <div className="w-5 h-5 rounded-full bg-[#147b56] text-white flex items-center justify-center text-[9px] font-bold">
                      {getUserInitials(owner)}
                    </div>
                    <span className="text-xs font-bold text-slate-900">{getUserFullName(owner)}</span>
                  </div>
                ) : (
                  <span className="text-xs text-slate-400">N/A</span>
                )}
              </div>
              <div className="flex justify-between items-start">
                <span className="text-xs font-semibold text-slate-500 mt-1">Collaborators</span>
                <div className="flex flex-col items-end gap-1.5 max-w-[200px]">
                  {collaborators.length > 0 ? (
                    collaborators.map((user, idx) => {
                      const initials = getUserInitials(user);
                      const avatarBg = getAvatarBgColor(initials);
                      const fullName = getUserFullName(user);
                      return (
                        <div
                          key={idx}
                          className="inline-flex items-center gap-1.5 bg-[#f7f6f3] hover:bg-[#efeee7] transition-colors pl-1 pr-2.5 py-1 rounded-full shadow-2xs"
                        >
                          <div className={`w-5 h-5 rounded-full ${avatarBg} text-white flex items-center justify-center text-[9px] font-bold shrink-0`}>
                            {initials}
                          </div>
                          <span className="text-xs font-bold text-slate-900 truncate max-w-[140px]">{fullName}</span>
                        </div>
                      );
                    })
                  ) : (
                    <span className="text-xs text-slate-400">None</span>
                  )}
                </div>
              </div>
            </div>
 
          </div>
        </div>
      </div>

      {/* Edit Work Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-[200] bg-slate-950/60 backdrop-blur-xs flex items-center justify-center p-3 sm:p-6 overflow-y-auto">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl w-full max-w-2xl max-h-[80vh] sm:max-h-[85vh] my-auto flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50 shrink-0">
              <div>
                <h3 className="text-lg font-bold text-slate-900">Edit Work Item</h3>
                <p className="text-xs text-slate-500 mt-0.5">ID: <span className="font-semibold text-slate-700">{displayId}</span></p>
              </div>
              <button 
                type="button"
                onClick={() => setShowEditModal(false)}
                className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 flex items-center justify-center transition-colors"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleEditFormSubmit} className="flex flex-col flex-1 overflow-hidden min-h-0">
              <div className="p-6 space-y-4 overflow-y-auto flex-1">
                {/* Task Name */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Task Name <span className="text-rose-500">*</span></label>
                  <input 
                    type="text"
                    required
                    value={editForm.task_name}
                    onChange={(e) => setEditForm({ ...editForm, task_name: e.target.value })}
                    placeholder="e.g. Build API Gateway v2"
                    className="w-full px-3.5 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 transition-colors text-slate-900"
                  />
                </div>

                {/* KPI Type & Points */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="mb-1.5">
                      <label className="block text-xs font-semibold text-slate-700">KPI Type</label>
                    </div>
                    <Select
                      isDisabled={true}
                      options={(taskTypes || []).map(t => ({ value: t._id, label: `${t.name} (${t.score || 0} pts)` }))}
                      className="text-sm text-slate-900"
                      classNamePrefix="select"
                      placeholder="Select type"
                      onChange={opt => handleEditTypeChange({ target: { value: opt ? opt.value : '' } })}
                      value={(taskTypes || []).filter(t => t._id === editForm.task_kpi_type_id).map(t => ({ value: t._id, label: `${t.name} (${t.score || 0} pts)` }))[0] || null}
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderColor: '#e2e8f0',
                          borderRadius: '0.75rem',
                          padding: '1px',
                          boxShadow: 'none',
                          '&:hover': {
                            borderColor: '#0f172a'
                          }
                        })
                      }}
                    />
                  </div>
                  <div>
                    <div className="mb-1.5">
                      <label className="block text-xs font-semibold text-slate-700">Assigned Points</label>
                    </div>
                    <input 
                      type="number"
                      disabled={true}
                      readOnly={true}
                      value={editForm.assigned_kpi_points}
                      placeholder="e.g. 50"
                      className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none text-slate-700 font-semibold bg-slate-100 cursor-not-allowed select-none"
                    />
                  </div>
                </div>

                {/* Priority & Due Date */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">Priority</label>
                    <div className="flex bg-slate-100 p-1 rounded-xl">
                      {["Low", "Medium", "High"].map((level) => (
                        <button
                          key={level}
                          type="button"
                          onClick={() => setEditForm({ ...editForm, task_priority: level })}
                          className={`flex-1 py-1 text-xs font-semibold rounded-lg transition-all ${editForm.task_priority === level ? 'bg-slate-900 text-white shadow-xs' : 'text-slate-500 hover:text-slate-900'}`}
                        >
                          {level}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                      Due Date <span className="text-rose-500">*</span>
                    </label>
                    <input 
                      type="date"
                      required
                      value={editForm.task_due_date}
                      onChange={(e) => setEditForm({ ...editForm, task_due_date: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 text-slate-900"
                    />
                  </div>
                </div>

                {/* Status */}
                {canEditStatus && (
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">Status</label>
                    <Select
                      options={statusOptions.map(opt => ({ value: opt.value, label: opt.label }))}
                      className="text-sm text-slate-900 font-medium"
                      classNamePrefix="select"
                      placeholder="Keep Current Status"
                      menuPortalTarget={document.body}
                      menuPlacement="auto"
                      onChange={opt => setEditForm({ ...editForm, task_status: opt ? opt.value : '' })}
                      value={statusOptions.filter(opt => opt.value === editForm.task_status).map(opt => ({ value: opt.value, label: opt.label }))[0] || null}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        control: (base) => ({
                          ...base,
                          borderColor: '#e2e8f0',
                          borderRadius: '0.75rem',
                          padding: '1px',
                          boxShadow: 'none',
                          '&:hover': {
                            borderColor: '#0f172a'
                          }
                        })
                      }}
                    />
                  </div>
                )}

                {/* Assignees */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Assigned To</label>
                  <Select
                    options={userOptions}
                    value={userOptions.find(opt => editForm.assigned_to.includes(opt.value)) || null}
                    placeholder={workProjectId ? "Select assignee..." : "No project associated"}
                    isDisabled={!workProjectId}
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      control: (base) => ({
                        ...base,
                        borderColor: '#e2e8f0',
                        borderRadius: '0.75rem',
                        padding: '1px',
                        boxShadow: 'none',
                        '&:hover': {
                          borderColor: '#0f172a'
                        }
                      })
                    }}
                    onChange={handleEditAssigneeChange}
                    className="text-sm"
                    classNamePrefix="select"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Description (Optional)</label>
                  <textarea 
                    rows="3"
                    value={editForm.task_description}
                    onChange={(e) => setEditForm({ ...editForm, task_description: e.target.value })}
                    placeholder="Add work details..."
                    className="w-full px-3.5 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 resize-none text-slate-900"
                  ></textarea>
                </div>
              </div>

              {/* Actions */}
              <div className="px-6 py-4 bg-slate-50/50 border-t border-slate-100 flex justify-end gap-3 shrink-0">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={editSubmitting || !editForm.task_name || !editForm.task_due_date}
                  className="px-5 py-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white rounded-xl text-xs font-bold shadow-xs transition-all flex items-center gap-2"
                >
                  {editSubmitting && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[9999] bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl w-full max-w-md p-6 animate-in fade-in zoom-in-95 duration-200">
            <div className="w-12 h-12 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center mb-4">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                <line x1="10" y1="11" x2="10" y2="17"></line>
                <line x1="14" y1="11" x2="14" y2="17"></line>
              </svg>
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-1.5">Delete Work Item?</h3>
            <p className="text-xs text-slate-500 mb-6 leading-relaxed">
              Are you sure you want to delete <span className="font-bold text-slate-800">"{title}"</span>? This work item will be removed from your KPI workspace.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteWork}
                disabled={deletingWork}
                className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-bold shadow-xs transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {deletingWork && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Complete Status Confirmation Modal */}
      {showCompleteConfirmModal && (
        <div className="fixed inset-0 z-[9999] bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl w-full max-w-md p-6 animate-in fade-in zoom-in-95 duration-200">
            <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center mb-4">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                <line x1="12" y1="9" x2="12" y2="13"></line>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
              </svg>
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-1.5">Confirm Status Change</h3>
            <p className="text-xs text-slate-600 mb-6 leading-relaxed">
              Once the status is changed to Completed, it cannot be changed again. Are you sure you want to proceed?
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowCompleteConfirmModal(false);
                  setPendingStatusOption(null);
                  setSelectedStatusOverride(null);
                }}
                className="px-4 py-2 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  if (pendingStatusOption) {
                    executeStatusChange(pendingStatusOption);
                  }
                }}
                disabled={statusUpdating}
                className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold shadow-xs transition-colors flex items-center gap-2 disabled:opacity-50 cursor-pointer"
              >
                {statusUpdating && <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>}
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KPIWorkDetails;

